import { useParams } from 'react-router-dom';
import { useMemo, useState, useCallback } from 'react';
import { Car } from 'lucide-react';
import { EntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig } from '@components/integrated/entity-form';
import { useClaimFlowAuthStore } from '../auth/store';
import { claimflowApiClient } from '../api/claimflowApiClient';
import type { ClaimFlowLoginResponse, ErpSession } from '../auth/types';

import { Card, VStack, HStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Select } from '@components/base/inputs';
import { Button } from '@components/base/actions';
import { Spinner, Alert } from '@components/base/feedback';

const TOKEN_KEY = 'claimflow_auth_token';
const TENANT_KEY = 'claimflow_active_tenant_id';

/** Shown when the post-login workflow failed and carried no server message. */
export const POST_LOGIN_ERROR_MESSAGE =
  "La récupération de vos sessions a échoué. Aucune session n'a été sélectionnée.";

/** Shown when the post-login workflow request never completed. */
export const POST_LOGIN_NETWORK_ERROR_MESSAGE =
  'Impossible de contacter le serveur pour récupérer vos sessions.';

/** Authentication data held until an ERP session is resolved. */
interface PendingAuth {
  token: string;
  user: ClaimFlowLoginResponse['data']['user'];
  tenants: ClaimFlowLoginResponse['data']['user']['tenants'];
  activeTenantId: string | null;
  sessions?: ErpSession[];
}

// ---------------------------------------------------------------------------
// Response readers (the workflow payload is untyped JSON)
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

/** Reads the server's own error message, when it sent a usable one. */
function readServerMessage(payload: Record<string, unknown> | null): string | null {
  const message = payload?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

/** Reads the ERP sessions returned by the workflow's `get-sessions` step. */
function readSessions(data: Record<string, unknown> | null): ErpSession[] | null {
  const contextData = asRecord(data?.context_data);
  const sessionStep = asRecord(contextData?.['get-sessions']);
  const erpResponse = asRecord(sessionStep?.erp_response);
  const sessions = erpResponse?.data;
  return Array.isArray(sessions) ? (sessions as ErpSession[]) : null;
}

export function LoginPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const [step, setStep] = useState<
    'login' | 'loading-sessions' | 'select-session' | 'session-error'
  >('login');
  const [availableSessions, setAvailableSessions] = useState<ErpSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<PendingAuth | null>(null);

  const loginFormConfig: EntityFormConfig = useMemo(() => ({
    id: 'claimflow-login',
    endpoint: {
      erpEndpoint: '/api/claimflow/auth/login',
      method: 'POST',
    },
    fields: {
      username: {
        type: 'text',
        label: 'Identifiant',
        placeholder: 'Entrez votre identifiant',
        order: 1,
        validation: { required: true },
      },
      password: {
        type: 'password',
        label: 'Mot de passe',
        placeholder: 'Entrez votre mot de passe',
        order: 2,
        validation: { required: true },
      },
      tenant_slug: {
        type: 'text',
        label: '',
        order: 3,
        defaultValue: tenantSlug ?? '',
      },
    },
    cssOverrides: {
      fieldOverrides: {
        tenant_slug: 'hidden',
      },
    },
    actions: {
      submit: {
        label: 'Se connecter',
        variant: 'primary',
        className: 'w-full',
      },
      cancel: { visible: false },
      reset: { visible: false },
    },
    workflow: {
      onError: {
        mapFieldErrors: true,
      },
    },
    layout: {
      columns: 1,
      gap: 'md',
    },
  }), [tenantSlug]);

  const finalizeAuth = useCallback((
    authData: { token: string; user: ClaimFlowLoginResponse['data']['user']; tenants: ClaimFlowLoginResponse['data']['user']['tenants']; activeTenantId: string | null },
    sessions?: ErpSession[],
  ) => {
    if (sessions?.length) {
      useClaimFlowAuthStore.getState().setAvailableSessions(sessions);
    }
    useClaimFlowAuthStore.setState({
      token: authData.token,
      user: authData.user,
      tenants: authData.tenants ?? [],
      activeTenantId: authData.activeTenantId,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  /**
   * Runs the post-login workflow that resolves the user's ERP sessions.
   *
   * A failure (HTTP error, `status: 'failed'`, or a network error) moves to the
   * `session-error` step instead of silently finalizing auth with no session —
   * which is what left users on an empty task list with nothing shown.
   */
  const runPostLoginWorkflow = useCallback((auth: PendingAuth) => {
    setSessionError(null);
    setStep('loading-sessions');

    fetch('/api/claimflow/workflows/claimflow-post-login/execute', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.token}`,
        ...(auth.activeTenantId ? { 'X-Tenant-ID': auth.activeTenantId } : {}),
      },
    })
      .then((res) =>
        res
          .json()
          .then((body: unknown) => ({ ok: res.ok, body }))
          // A failed response with an empty/non-JSON body is still a failure.
          .catch(() => ({ ok: res.ok, body: null as unknown })),
      )
      .then(
        ({ ok, body }) => {
          const payload = asRecord(body);
          const data = asRecord(payload?.data);

          // Origami answers HTTP 422 with { message, data: { status: 'failed' } }
          // when a workflow step aborts, so `.catch()` alone never sees it.
          if (!ok || data?.status === 'failed') {
            setSessionError(
              readServerMessage(payload) ?? POST_LOGIN_ERROR_MESSAGE,
            );
            setStep('session-error');
            return;
          }

          const sessions = readSessions(data);

          if (sessions && sessions.length === 1) {
            const s = sessions[0]!;
            useClaimFlowAuthStore.getState().setSpoofedSessionId(
              s.session,
              String(s.lib ?? s.session),
            );
            finalizeAuth(auth, sessions);
          } else if (sessions && sessions.length > 1) {
            setAvailableSessions(sessions);
            setAuthData({ ...auth, sessions });
            setStep('select-session');
          } else {
            // Legitimate outcome: the request succeeded, the user has no session.
            finalizeAuth(auth);
          }
        },
        () => {
          // Rejection handler, NOT a trailing .catch(): only a request that
          // never completed (network / CORS / abort) lands here. An error
          // thrown while applying the result above must not be mislabelled as
          // a session failure — it stays a real, loud error.
          setSessionError(POST_LOGIN_NETWORK_ERROR_MESSAGE);
          setStep('session-error');
        },
      );
  }, [finalizeAuth]);

  const handleSuccess = (response: unknown) => {
    const loginResponse = response as ClaimFlowLoginResponse;
    const { token, user } = loginResponse.data;
    const tenants = user.tenants ?? [];
    localStorage.setItem(TOKEN_KEY, token);
    const persisted = localStorage.getItem(TENANT_KEY);
    const firstTenant = tenants[0];
    const activeTenantId =
      persisted ?? (firstTenant ? String(firstTenant.id) : null);
    if (activeTenantId) {
      localStorage.setItem(TENANT_KEY, activeTenantId);
    }

    // Do NOT set isAuthenticated yet — the auth guard would unmount LoginPage
    const authData: PendingAuth = { token, user, tenants, activeTenantId };
    setAuthData(authData);

    // Fetch ERP sessions via post-login workflow
    runPostLoginWorkflow(authData);
  };

  const sessionOptions = useMemo(
    () => availableSessions.map((s) => ({
      value: String(s.session),
      label: `${s.lib ?? s.session} (${s.session})`,
    })),
    [availableSessions],
  );

  const handleSessionConfirm = useCallback(() => {
    const session = availableSessions.find((s) => String(s.session) === selectedSessionId);
    if (session) {
      useClaimFlowAuthStore.getState().setSpoofedSessionId(
        session.session,
        String(session.lib ?? session.session),
      );

      // Fire-and-forget: capture CD_UTIL from def-user-login.
      // Its failure is harmless here, so it opts out of the global error toast.
      claimflowApiClient.get(
        '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-def-user-login',
        { params: { SES: session.session }, suppressErrorToast: true },
      ).then((response) => {
        const items = response.data?.data?.data;
        if (Array.isArray(items) && items.length > 0) {
          const userRecord = items[0];
          const cdUtil = String(userRecord.CD_UTIL_SESSION ?? userRecord.CD_UTIL ?? '');
          if (cdUtil) {
            useClaimFlowAuthStore.getState().setCdUtil(cdUtil);
          }
        }
      }).catch(() => {
        // Fire-and-forget: login endpoint failure should not block the UI
      });
    }
    if (authData) {
      finalizeAuth(authData, authData.sessions);
    }
  }, [availableSessions, selectedSessionId, authData, finalizeAuth]);

  const handleRetrySessions = useCallback(() => {
    if (authData) {
      runPostLoginWorkflow(authData);
    }
  }, [authData, runPostLoginWorkflow]);

  const handleContinueAnyway = useCallback(() => {
    if (authData) {
      finalizeAuth(authData, authData.sessions);
    }
  }, [authData, finalizeAuth]);

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
      <Card variant="elevated" padding="lg" className="w-full max-w-md">
        <Card.Content>
          <VStack gap={6}>
            <VStack gap={2} align="center">
              <HStack gap={2} align="center">
                <Icon icon={Car} size="lg" color="primary" />
                <Text as="h1" size="xl" weight="bold" color="primary">
                  ClaimFlow
                </Text>
              </HStack>
              <Text size="sm" color="muted">
                {step === 'select-session'
                  ? 'Sélectionnez votre session pour continuer'
                  : 'Connectez-vous pour accéder à la gestion des sinistres'}
              </Text>
            </VStack>

            {step === 'login' && (
              <EntityForm config={loginFormConfig} onSuccess={handleSuccess}>
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            )}

            {step === 'loading-sessions' && (
              <VStack gap={4} align="center">
                <Spinner size="md" />
                <Text size="sm" color="muted">
                  Chargement des sessions...
                </Text>
              </VStack>
            )}

            {step === 'session-error' && (
              <VStack gap={4}>
                <Alert variant="error" title="Session indisponible">
                  {sessionError ?? POST_LOGIN_ERROR_MESSAGE}
                </Alert>
                <HStack gap={2}>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleRetrySessions}
                  >
                    Réessayer
                  </Button>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={handleContinueAnyway}
                  >
                    Continuer quand même
                  </Button>
                </HStack>
              </VStack>
            )}

            {step === 'select-session' && (
              <VStack gap={4}>
                <Select
                  label="Session"
                  options={sessionOptions}
                  value={selectedSessionId}
                  onChange={(value) => {
                    const v = Array.isArray(value) ? value[0] : value;
                    setSelectedSessionId(v ?? '');
                  }}
                  placeholder="Sélectionner une session..."
                  size="md"
                />
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={!selectedSessionId}
                  onClick={handleSessionConfirm}
                >
                  Continuer
                </Button>
              </VStack>
            )}
          </VStack>
        </Card.Content>
      </Card>
    </div>
  );
}
